/**
 * Tabs Layout Component
 *
 * Main component that renders the tab bar and manages tab navigation.
 * Features:
 * - Fixed tab bar positioned below header, spanning full viewport width
 * - Drag-and-drop reordering
 * - Content caching (keep-alive)
 * - Settings modal
 * - Back/forward navigation
 */

import { useTabsContext } from '@/context/TabsContext';
import { ROOT_PANE_ID } from '@/context/TabsContext/reducer';
import {
  loadUserSettings,
  saveUserSettings,
  type UserTabSettings,
} from '@/context/TabsContext/settings';
import type { PaneId, TabItem } from '@/context/TabsContext/types';
import { getRouteTitle, getTabDisplayTitle } from '@/utils/routeUtils';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable';
import {
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useCommandPaletteContext } from '@/context/CommandPaletteContext';
import { Button, Splitter, Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DraggableTab } from './DraggableTab';
import { TabsSettingsModal } from './TabsSettingsModal';
import { TabsOverflowMenu } from './TabsOverflowMenu';
import { PortalKeepAliveOutlet } from './KeepAliveOutlet';
import { PageContextBar } from './PageContextBar';
import './TabsLayout.less';

/**
 * Fixed Tab Bar Component - renders at the top of viewport below header
 */
const PaneTabBar: React.FC<{
  paneId: PaneId;
  paneTabs: TabItem[];
  paneActiveTabKey: string | null;
  isFocused: boolean;
  onOpenSettings: () => void;
  userSettings: UserTabSettings;
}> = ({ paneId, paneTabs, paneActiveTabKey, isFocused, onOpenSettings, userSettings }) => {
  const {
    tabs,
    focusTab,
    focusPane,
    closeTab,
    goBack,
    goForward,
    openTab,
    navigateInTab,
    pinTab,
    unpinTab,
    config,
    panes,
  } = useTabsContext();

  const { openForNewTab } = useCommandPaletteContext();

  const location = useLocation();
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const {
    isOver: isPaneDropTarget,
    setNodeRef: setPaneDropRef,
  } = useDroppable({
    id: `pane-drop-${paneId}`,
    data: { type: 'pane', paneId },
    disabled: !config.enableDragReorder,
  });
  const [visibleTabs, setVisibleTabs] = useState<TabItem[]>(paneTabs);
  const [hiddenTabs, setHiddenTabs] = useState<TabItem[]>([]);
  const lastPathRef = useRef<string>('');
  const lastSearchRef = useRef<string>('');
  const isInitialMount = useRef(true);
  const isUpdatingRef = useRef(false);
  const paneActiveTab = useMemo(
    () => paneTabs.find((tab) => tab.key === paneActiveTabKey) || null,
    [paneTabs, paneActiveTabKey]
  );
  const canGoBack = useMemo(
    () => Boolean(paneActiveTab && paneActiveTab.historyIndex > 0),
    [paneActiveTab]
  );
  const canGoForward = useMemo(
    () => Boolean(paneActiveTab && paneActiveTab.historyIndex < paneActiveTab.history.length - 1),
    [paneActiveTab]
  );

  // Store callbacks in refs to avoid triggering effect on callback changes
  const openTabRef = useRef(openTab);
  const focusTabRef = useRef(focusTab);
  const navigateInTabRef = useRef(navigateInTab);

  useEffect(() => {
    openTabRef.current = openTab;
    focusTabRef.current = focusTab;
    navigateInTabRef.current = navigateInTab;
  });

  // Handle route changes from external navigation (including initial load)
  useEffect(() => {
    // Skip if we're currently updating to prevent loop
    if (isUpdatingRef.current) return;
    
    const currentPath = location.pathname;
    const currentSearch = location.search;

    if (!isFocused || paneId !== ROOT_PANE_ID) return;

    // Skip if same path AND same search (but not on initial mount - we need to sync on first load)
    if (!isInitialMount.current && lastPathRef.current === currentPath && lastSearchRef.current === currentSearch) return;
    
    isInitialMount.current = false;
    lastPathRef.current = currentPath;
    lastSearchRef.current = currentSearch;

    // Find existing tab by checking both base path and current history entry
    const existingTab = paneTabs.find((t) => {
      // Check if tab's base path matches
      if (t.path === currentPath) {
        // If there's a search param, also check if it matches
        if (currentSearch) {
          const currentEntry = t.history[t.historyIndex];
          return currentEntry?.search === currentSearch;
        }
        return true;
      }
      // Check if tab's current history entry matches
      const currentEntry = t.history[t.historyIndex];
      if (currentEntry?.path === currentPath) {
        // If there's a search param, also check if it matches
        if (currentSearch) {
          return currentEntry?.search === currentSearch;
        }
        return true;
      }
      return false;
    });

    const matchesCurrentLocation = (tab: TabItem) => {
      if (tab.path === currentPath) {
        if (currentSearch) {
          const currentEntry = tab.history[tab.historyIndex];
          return currentEntry?.search === currentSearch;
        }
        return true;
      }

      const currentEntry = tab.history[tab.historyIndex];
      if (currentEntry?.path === currentPath) {
        if (currentSearch) {
          return currentEntry.search === currentSearch;
        }
        return true;
      }

      return false;
    };

    if (existingTab) {
      // Check if search params match
      const currentEntry = existingTab.history[existingTab.historyIndex];
      const entrySearch = currentEntry?.search || '';

      if (existingTab.key !== paneActiveTabKey) {
        focusTabRef.current(existingTab.key, paneId);
      }

      // If search params don't match, navigate within the tab to update them
      // Use replace: true to avoid creating new history entry and prevent loop
      if (currentSearch !== entrySearch) {
        const title = getRouteTitle(currentPath);
        // Set updating flag to prevent re-triggering this effect
        isUpdatingRef.current = true;
        // Update refs immediately to prevent loop
        lastPathRef.current = currentPath;
        lastSearchRef.current = currentSearch;
        navigateInTabRef.current(currentPath, { title, search: currentSearch, replace: true, paneId });
        // Reset flag in next tick to allow React Router to update without triggering loop
        Promise.resolve().then(() => {
          isUpdatingRef.current = false;
        });
      }

      return;
    }

    const urlOwnedByAnotherPane = panes.some((pane) =>
      pane.id !== paneId
      && pane.tabKeys.some((tabKey) => {
        const tab = tabs.find((item) => item.key === tabKey);
        return tab ? matchesCurrentLocation(tab) : false;
      })
    );
    if (urlOwnedByAnotherPane) return;

    const activeTab = paneTabs.find((t) => t.key === paneActiveTabKey);
    if (activeTab) {
      // Only navigate within the tab if this is a detail page of the current path
      // e.g., /users -> /users/123 (detail page)
      // NOT /manual/cases -> /manual/overview (different pages at same level)
      const activeTabCurrentPath = activeTab.history[activeTab.historyIndex]?.path || activeTab.path;
      const isDetailPage = currentPath.startsWith(activeTabCurrentPath + '/');
      
      if (isDetailPage) {
        const title = getRouteTitle(currentPath);
        navigateInTabRef.current(currentPath, { title, search: currentSearch, paneId });
        return;
      }
    }

    const title = getRouteTitle(currentPath);
    openTabRef.current(currentPath, title, { search: currentSearch, paneId });
  }, [location.pathname, location.search, paneTabs, paneActiveTabKey, isFocused, paneId, panes, tabs]);

  // Handle tab visibility based on container width
  useEffect(() => {
    const updateVisibleTabs = () => {
      if (!tabsContainerRef.current) {
        setVisibleTabs(paneTabs);
        setHiddenTabs([]);
        return;
      }

      const containerWidth = tabsContainerRef.current.offsetWidth;
      const navigationWidth = 100;
      const overflowButtonWidth = 50;
      const settingsButtonWidth = 40;
      const availableWidth = containerWidth - navigationWidth - overflowButtonWidth - settingsButtonWidth;

      const pinnedTabWidth = 100;
      const regularTabWidth = 150;
      const minTabWidth = 80;

      let usedWidth = 0;
      const visible: TabItem[] = [];
      const hidden: TabItem[] = [];

      for (const tab of paneTabs) {
        const tabWidth = tab.pinned ? pinnedTabWidth : regularTabWidth;
        if (usedWidth + minTabWidth <= availableWidth) {
          visible.push(tab);
          usedWidth += tabWidth;
        } else {
          hidden.push(tab);
        }
      }

      const activeTab = paneTabs.find((t) => t.key === paneActiveTabKey);
      if (activeTab && hidden.includes(activeTab)) {
        const lastVisible = [...visible].reverse().find((t) => !t.pinned);
        if (lastVisible) {
          const lastVisibleIndex = visible.indexOf(lastVisible);
          visible.splice(lastVisibleIndex, 1);
          hidden.unshift(lastVisible);
          visible.push(activeTab);
          const activeIndex = hidden.indexOf(activeTab);
          hidden.splice(activeIndex, 1);
        }
      }

      setVisibleTabs(visible);
      setHiddenTabs(hidden);
    };

    updateVisibleTabs();

    const resizeObserver = new ResizeObserver(updateVisibleTabs);
    if (tabsContainerRef.current) {
      resizeObserver.observe(tabsContainerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [paneTabs, paneActiveTabKey]);

  // Handle tab focus
  // Note: focusTab already updates browser URL via window.history.replaceState
  // so we don't need to call history.replace here (would cause double navigation)
  const handleTabFocus = useCallback(
    (key: string) => {
      focusTab(key, paneId);
    },
    [focusTab, paneId]
  );

  // Handle double-click for pin toggle
  const handleTabDoubleClick = useCallback(
    (key: string) => {
      if (userSettings.doubleClickAction === 'pin') {
        const tab = tabs.find((t) => t.key === key);
        if (tab) {
          if (tab.pinned) {
            unpinTab(key);
          } else {
            pinTab(key);
          }
        }
      }
    },
    [tabs, userSettings.doubleClickAction, pinTab, unpinTab]
  );

  const pinnedTabs = useMemo(() => visibleTabs.filter((t) => t.pinned), [visibleTabs]);
  const regularTabs = useMemo(() => visibleTabs.filter((t) => !t.pinned), [visibleTabs]);
  const allTabIds = useMemo(() => visibleTabs.map((t) => t.key), [visibleTabs]);
  const setTabsContainerNode = useCallback((element: HTMLDivElement | null) => {
    tabsContainerRef.current = element;
    setPaneDropRef(element);
  }, [setPaneDropRef]);

  // Use portal to render tab bar at document body level (fixed positioning)
  const tabBarContent = (
    <div
      className={`tabs-layout-bar-fixed ${isFocused ? 'tabs-layout-bar-focused' : ''} ${isPaneDropTarget ? 'tabs-layout-bar-drop-target' : ''}`}
      ref={setTabsContainerNode}
      data-testid="tabs-pane-tabbar"
      data-pane-id={paneId}
      onMouseDown={() => focusPane(paneId)}
    >
      {/* Navigation buttons */}
      <div className="tabs-layout-navigation">
        <Tooltip title={canGoBack ? 'Go back (Alt+←)' : 'No history'} mouseEnterDelay={0.5}>
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            disabled={!canGoBack}
            onClick={() => goBack(paneId)}
            className="tabs-layout-nav-btn"
          />
        </Tooltip>
        <Tooltip title={canGoForward ? 'Go forward (Alt+→)' : 'No forward history'} mouseEnterDelay={0.5}>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            disabled={!canGoForward}
            onClick={() => goForward(paneId)}
            className="tabs-layout-nav-btn"
          />
        </Tooltip>
      </div>

      {/* Tabs with drag-and-drop */}
      <SortableContext id={paneId} items={allTabIds} strategy={horizontalListSortingStrategy}>
        {/* Pinned tabs */}
        {pinnedTabs.length > 0 && (
          <div className="tabs-layout-pinned-section">
            {pinnedTabs.map((tab) => (
              <div
                key={tab.key}
                onDoubleClick={() => handleTabDoubleClick(tab.key)}
              >
                <DraggableTab
                  tab={tab}
                  isActive={tab.key === paneActiveTabKey}
                  onFocus={() => handleTabFocus(tab.key)}
                  onClose={() => closeTab(tab.key)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Divider */}
        {pinnedTabs.length > 0 && regularTabs.length > 0 && (
          <div className="tabs-layout-divider" />
        )}

        {/* Regular tabs */}
        <div className="tabs-layout-regular-section">
          {regularTabs.map((tab) => (
            <div
              key={tab.key}
              onDoubleClick={() => handleTabDoubleClick(tab.key)}
            >
              <DraggableTab
                tab={tab}
                isActive={tab.key === paneActiveTabKey}
                onFocus={() => handleTabFocus(tab.key)}
                onClose={() => closeTab(tab.key)}
              />
            </div>
          ))}
        </div>
      </SortableContext>

      {/* Overflow menu */}
      {hiddenTabs.length > 0 && (
        <TabsOverflowMenu hiddenTabs={hiddenTabs} paneId={paneId} activeTabKey={paneActiveTabKey} />
      )}

      {/* New Tab button */}
      <div className="tabs-layout-new-tab">
        <Tooltip title="New Tab" mouseEnterDelay={0.5}>
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              focusPane(paneId);
              openForNewTab();
            }}
            className="tabs-layout-new-tab-btn"
          />
        </Tooltip>
      </div>

      {/* Settings button */}
      <div className="tabs-layout-settings">
        <Tooltip title="Tab Settings" mouseEnterDelay={0.5}>
          <Button
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={onOpenSettings}
            className="tabs-layout-settings-btn"
          />
        </Tooltip>
      </div>
    </div>
  );

  // Render directly - positioned fixed within the layout
  return tabBarContent;
};

const PaneContentHost: React.FC<{
  paneId: PaneId;
  setPaneContentContainer: (paneId: PaneId, element: HTMLDivElement | null) => void;
}> = ({ paneId, setPaneContentContainer }) => {
  const setRef = useCallback((element: HTMLDivElement | null) => {
    setPaneContentContainer(paneId, element);
  }, [paneId, setPaneContentContainer]);

  return (
    <div
      className="tabs-layout-content-wrapper tabs-layout-content-container"
      ref={setRef}
    />
  );
};

/**
 * Inner layout component (uses context)
 * 
 * Uses KeepAliveOutlet to render page components without re-mounting on tab switch.
 */
const TabsLayoutInner: React.FC = () => {
  const {
    tabs,
    panes,
    activePaneId,
    splitOrientation,
    focusPane,
    closeTab,
    config,
    dispatch,
    pinTab,
    unpinTab,
  } = useTabsContext();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userSettings, setUserSettings] = useState<UserTabSettings>(loadUserSettings);
  const [activeDragKey, setActiveDragKey] = useState<string | null>(null);
  const [splitterSizes, setSplitterSizes] = useState<number[]>([]);
  const paneContainersRef = useRef<Map<PaneId, HTMLDivElement>>(new Map());
  const [paneContainerVersion, forcePaneContainerRender] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleSettingsChange = useCallback((newSettings: UserTabSettings) => {
    setUserSettings(newSettings);
    saveUserSettings(newSettings);
  }, []);

  const paneItems = useMemo(() => {
    return panes.map((pane) => ({
      pane,
      tabs: pane.tabKeys
        .map((tabKey) => tabs.find((tab) => tab.key === tabKey))
        .filter((tab): tab is TabItem => Boolean(tab)),
    }));
  }, [panes, tabs]);

  const visiblePaneDefaultSize = `${100 / Math.max(panes.length, 1)}%`;
  const paneLayoutSignature = useMemo(
    () => `${splitOrientation}:${panes.map((pane) => pane.id).join('|')}`,
    [panes, splitOrientation]
  );
  const controlledSplitterSizes = splitterSizes.length === paneItems.length ? splitterSizes : [];
  const activeDragTab = useMemo(
    () => tabs.find((tab) => tab.key === activeDragKey) || null,
    [activeDragKey, tabs]
  );

  useEffect(() => {
    setSplitterSizes([]);
  }, [paneLayoutSignature]);

  const getTabUrl = useCallback((tab: TabItem): string => {
    const currentEntry = tab.history[tab.historyIndex];
    const targetPath = currentEntry?.path || tab.path;
    const targetSearch = currentEntry?.search || '';
    return targetSearch ? `${targetPath}${targetSearch}` : targetPath;
  }, []);

  const getFallbackTabUrlAfterMove = useCallback((sourcePaneId: PaneId, tabKey: string): string => {
    const sourcePane = panes.find((pane) => pane.id === sourcePaneId);
    if (!sourcePane) return '/dashboard';

    const removedIndex = sourcePane.tabKeys.findIndex((key) => key === tabKey);
    const remainingTabKeys = sourcePane.tabKeys.filter((key) => key !== tabKey);
    const fallbackTabKey = remainingTabKeys[removedIndex - 1] || remainingTabKeys[removedIndex] || remainingTabKeys[0];
    const fallbackTab = tabs.find((tab) => tab.key === fallbackTabKey);
    return fallbackTab ? getTabUrl(fallbackTab) : '/dashboard';
  }, [getTabUrl, panes, tabs]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragKey(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveDragKey(null);
      if (!over || active.id === over.id) return;

      const activeKey = String(active.id);
      const overKey = String(over.id);
      const sourcePane = panes.find((pane) => pane.tabKeys.includes(activeKey));
      const overData = over.data.current as { type?: string; paneId?: PaneId } | undefined;
      const targetPane = overData?.type === 'pane' && overData.paneId
        ? panes.find((pane) => pane.id === overData.paneId)
        : panes.find((pane) => pane.tabKeys.includes(overKey));
      const fromTab = tabs.find((tab) => tab.key === activeKey);
      const toTab = tabs.find((tab) => tab.key === overKey);

      if (!sourcePane || !targetPane || !fromTab) return;

      if (toTab && fromTab.pinned !== toTab.pinned) {
        if (fromTab.pinned && !toTab.pinned) {
          unpinTab(fromTab.key);
        } else if (!fromTab.pinned && toTab.pinned) {
          pinTab(fromTab.key);
        }
      }

      const targetTabKeys = targetPane.tabKeys.filter((key) => key !== activeKey);
      const targetIndex = toTab ? Math.max(0, targetTabKeys.indexOf(overKey)) : targetTabKeys.length;

      dispatch({
        type: 'MOVE_TAB_TO_PANE',
        payload: {
          key: activeKey,
          paneId: targetPane.id,
          toIndex: targetIndex,
        },
      });

      if (targetPane.id === ROOT_PANE_ID) {
        navigate(getTabUrl(fromTab), { replace: true });
        return;
      }

      if (sourcePane.id === ROOT_PANE_ID) {
        navigate(getFallbackTabUrlAfterMove(sourcePane.id, activeKey), { replace: true });
      }
    },
    [dispatch, getFallbackTabUrlAfterMove, getTabUrl, navigate, panes, pinTab, tabs, unpinTab]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragKey(null);
  }, []);

  const setPaneContentContainer = useCallback((paneId: PaneId, element: HTMLDivElement | null) => {
    const current = paneContainersRef.current.get(paneId) || null;
    if (current === element) return;

    if (element) {
      paneContainersRef.current.set(paneId, element);
    } else {
      paneContainersRef.current.delete(paneId);
    }
    forcePaneContainerRender((value) => value + 1);
  }, []);

  const renderPane = (pane: typeof paneItems[number]) => (
    <div
      key={pane.pane.id}
      className={`tabs-layout-pane ${pane.pane.id === activePaneId ? 'tabs-layout-pane-focused' : ''}`}
      data-testid="tabs-pane"
      onMouseDown={() => focusPane(pane.pane.id)}
    >
      <div className="tabs-layout-bar-wrapper">
        <PaneTabBar
          paneId={pane.pane.id}
          paneTabs={pane.tabs}
          paneActiveTabKey={pane.pane.activeTabKey}
          isFocused={pane.pane.id === activePaneId}
          onOpenSettings={() => setSettingsOpen(true)}
          userSettings={userSettings}
        />
      </div>

      <PaneContentHost
        paneId={pane.pane.id}
        setPaneContentContainer={setPaneContentContainer}
      />
    </div>
  );

  return (
    <>
      <PageContextBar />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <Splitter
          className="tabs-layout-splitter"
          layout={splitOrientation}
          onResize={setSplitterSizes}
        >
          {paneItems.map((pane, index) => (
            <Splitter.Panel
              key={pane.pane.id}
              min="260px"
              size={controlledSplitterSizes[index]}
              defaultSize={visiblePaneDefaultSize}
            >
              {renderPane(pane)}
            </Splitter.Panel>
          ))}
        </Splitter>

        <PortalKeepAliveOutlet
          tabs={tabs}
          panes={panes}
          closeTab={closeTab}
          config={config}
          paneContainers={paneContainersRef.current}
          paneContainerVersion={paneContainerVersion}
        />

        <TabsSettingsModal
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          onSettingsChange={handleSettingsChange}
        />

        <DragOverlay dropAnimation={null}>
          {activeDragTab ? (
            <div
              className={`tabs-layout-drag-overlay ${activeDragTab.pinned ? 'tabs-layout-tab-pinned' : ''}`}
              data-testid="tabs-layout-drag-overlay"
            >
              {getTabDisplayTitle(activeDragTab)}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};

/**
 * Main Tabs Layout Component
 *
 * Note: TabsProvider should be at a higher level (rootContainer in app.tsx)
 * so that header components can also access tab navigation.
 * 
 * This component no longer accepts children - it manages its own page rendering
 * via KeepAliveOutlet to prevent unnecessary re-mounts when switching tabs.
 */
export const TabsLayout: React.FC = () => {
  return <TabsLayoutInner />;
};

export default TabsLayout;
